// @/utils/cookieConsentUtil.js
const COOKIE_CONSENT_KEY = 'cookie_consent_preferences';

/**
 * @typedef {Object} CookiePreferences
 * @property {boolean} essential - Always true, essential cookies
 * @property {boolean} analytics - Analytics cookies
 * @property {boolean} advertising - Advertising cookies
 */

/**
 * @typedef {function(CookiePreferences): void} ConsentListener
 * Listener for consent preference changes
 */

/**
 * Singleton for managing cookie consent
 */
class ConsentManager {
  constructor() {
    if (ConsentManager.instance) {
      return ConsentManager.instance;
    }
    
    ConsentManager.instance = this;
    /** @type {ConsentListener[]} */
    this.listeners = [];
    this.initialized = false;
    /** @type {CookiePreferences|null} */
    this.preferences = null;
  }

  /**
   * Initialize the consent manager
   * Loads saved preferences or sets defaults (with no consent by default)
   */
  initialize() {
    if (this.initialized) return;
    
    // Load saved preferences from storage or set defaults
    const savedPreferences = this.loadPreferences();
    
    // If we have saved preferences, use them
    // Otherwise, set defaults with NO CONSENT for analytics/advertising until user chooses
    this.preferences = savedPreferences || {
      essential: true,    // Essential cookies always enabled
      analytics: false,   // Default is NO consent for analytics
      advertising: false  // Default is NO consent for advertising
    };
    
    this.initialized = true;
    this.notifyListeners();
  }

  /**
   * Ensures the manager is initialized
   */
  ensureInitialized() {
    if (!this.initialized) {
      this.initialize();
    }
  }

  /**
   * Checks if consent is given for a cookie type
   * @param {string} type - Cookie type ('essential', 'analytics', 'advertising')
   * @returns {boolean} - Is consent given
   */
  hasConsent(type) {
    this.ensureInitialized();
    
    // Essential cookies are always allowed
    if (type === 'essential') return true;
    
    return !!this.preferences[type];
  }

  /**
   * Gets all current consent preferences
   * @returns {Object} - Current preferences
   */
  getPreferences() {
    this.ensureInitialized();
    return { ...this.preferences };
  }

  /**
   * Updates preferences and notifies listeners
   * @param {Object} preferences - Preferences object
   */
  updatePreferences(preferences) {
    this.ensureInitialized();
    
    // Merge existing preferences with new ones
    this.preferences = {
      ...this.preferences,
      ...preferences
    };
    
    // Always set essential to true
    this.preferences.essential = true;
    
    // Save preferences
    this.savePreferences();
    
    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Give consent to all cookie types
   */
  acceptAll() {
    this.updatePreferences({
      essential: true,
      analytics: true,
      advertising: true
    });
  }

  /**
   * Add a listener for preference changes
   * @param {Function} listener - Listener function
   * @returns {Function} - Unsubscribe function
   */
  subscribe(listener) {
    this.ensureInitialized();
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of preference changes
   */
  notifyListeners() {
    for (const listener of this.listeners) {
      try {
        listener(this.getPreferences());
      } catch (error) {
        console.error('Error in consent listener:', error);
      }
    }
  }

  /**
   * Load preferences from localStorage
   * @returns {Object|null} - Loaded preferences or null
   */
  loadPreferences() {
    try {
      if (typeof window === 'undefined') return null;
      
      const savedPreferences = localStorage.getItem(COOKIE_CONSENT_KEY);
      return savedPreferences ? JSON.parse(savedPreferences) : null;
    } catch (error) {
      console.error('Error loading cookie preferences:', error);
      return null;
    }
  }

  /**
   * Save preferences to localStorage
   */
  savePreferences() {
    try {
      if (typeof window === 'undefined') return;
      
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(this.preferences));
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  }

  /**
   * Check if the banner should be shown
   * @returns {boolean} - Should show banner
   */
  shouldShowBanner() {
    this.ensureInitialized();
    // Show banner if no preferences were saved yet
    return !this.loadPreferences();
  }
  
  /**
   * Checks if user has made a choice about cookies
   * @returns {boolean} - Has user made a choice
   */
  hasUserMadeChoice() {
    return !!this.loadPreferences();
  }
}

// Create and export global singleton instance
export const consentManager = new ConsentManager();

// Export convenience functions
export const hasConsent = (type) => consentManager.hasConsent(type);
export const getPreferences = () => consentManager.getPreferences();
export const updatePreferences = (prefs) => consentManager.updatePreferences(prefs);
export const acceptAll = () => consentManager.acceptAll();
export const shouldShowBanner = () => consentManager.shouldShowBanner();
export const hasUserMadeChoice = () => consentManager.hasUserMadeChoice();
export const subscribeToConsentChanges = (listener) => consentManager.subscribe(listener);